/** @jsxImportSource @emotion/react */
import { ClassNames, css } from '@emotion/react';
import escapeStringRegexp from 'escape-string-regexp';
import React, { useState } from 'react';

import Autocomplete from 'src/components/forms/controls/autocomplete';
import EditableModel from 'src/components/forms/editableModel';
import { colors } from 'src/util/colors';

interface SelectionOption {
  value: string;
  label: string;
}

export const SelectableModel = ({
  currentSchemaTypes,
  handleCancel,
  handleSelection,
  handleSubmit,
  handleSubmitSuccess,
  labelFn,
  rendererFn,
  savingDisabled = false,
  schemaBridgeFn,
  schemaFn,
  schemaNames,
}: {
  currentSchemaTypes: string[];
  handleCancel?: () => void;
  handleSelection?: (selection: SelectionOption) => Record<string, any>;
  handleSubmit?: (selectedSchema: string, model: Record<string, any>) => void;
  handleSubmitSuccess?: () => void;
  labelFn: (schemaKey: string) => string;
  rendererFn: (selectedSchema: string) => React.ComponentType<any>;
  savingDisabled?: boolean;
  schemaBridgeFn?: (selectedSchema: string) => any;
  schemaFn: (selectedSchema: string) => any;
  schemaNames: string[];
}) => {
  const [state, setState] = useState<{
    edited: boolean;
    selection: SelectionOption | null;
    selectedSchema: string | null;
    model: Record<string, any>;
  }>({
    edited: false,
    selection: null,
    selectedSchema: null,
    model: {},
  });

  const handleSelectionChange = (selection: SelectionOption) => {
    setState({
      model: handleSelection ? handleSelection(selection) : {},
      selection,
      selectedSchema: selection.value,
      edited: state.edited,
    });
  };

  const handleSubmitModel = (model: Record<string, any>) => {
    setState({ ...state, model });
    return handleSubmit?.(state.selectedSchema as string, model);
  };

  const handleSubmitSuccessCallback = () => {
    return handleSubmitSuccess?.();
  };

  const handleChangeCallback = () => {
    if (!state.edited) {
      setState({
        ...state,
        edited: true,
      });
    }
  };

  const handleCancelCallback = () => {
    return handleCancel?.();
  };

  const { edited, selectedSchema, selection, model } = state;
  const RenderComponent = selectedSchema ? rendererFn(selectedSchema) : null;

  // Don't show a schema if it's already been configured in the visit notes
  const sortedOptions = schemaNames
    .map(schemaKey =>
      currentSchemaTypes.includes(schemaKey)
        ? null
        : {
            value: schemaKey,
            label: labelFn(schemaKey),
          },
    )
    .filter((result): result is SelectionOption => result !== null)
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div css={containerStyles}>
      {!edited && (
        <div css={headerContainerStyles}>
          <ClassNames>
            {({ css: createClassName }) => (
              <Autocomplete
                classes={{
                  singleValue: createClassName(editableHeaderStyles),
                  placeholder: createClassName(editablePlaceholderStyles),
                }}
                variant="h6"
                options={sortedOptions}
                onChange={newSelection => handleSelectionChange(newSelection)}
                filterOption={(option, inputValue) => {
                  const escapedString = escapeStringRegexp(inputValue);
                  // Allow for
                  //  - Search on both the label and the value
                  //  - Close matches even when special characters are omitted
                  return (
                    !!option.label
                      .replace(/[^\w\s]/gi, '')
                      .toLowerCase()
                      .match(escapedString.toLowerCase()) ||
                    !!option.value.toLowerCase().match(escapedString.toLowerCase()) ||
                    !!option.label.toLowerCase().match(escapedString.toLowerCase())
                  );
                }}
                value={selection}
              />
            )}
          </ClassNames>
        </div>
      )}
      {selectedSchema && model && RenderComponent && (
        <EditableModel
          initializeEditing
          hideHeader={!edited}
          model={model}
          schema={schemaBridgeFn ? schemaBridgeFn(selectedSchema) : null}
          onCancel={handleCancelCallback}
          onSubmit={handleSubmitModel}
          handleSaveSuccess={handleSubmitSuccessCallback}
          handleChange={handleChangeCallback}
          savingDisabled={savingDisabled}
        >
          <RenderComponent model={model} schema={schemaFn(selectedSchema)} toplevel />
        </EditableModel>
      )}
    </div>
  );
};

const containerStyles = css`
  margin-bottom: 30px;
`;

const editableHeaderStyles = css`
  color: ${colors.taupe};
  font-size: 1.25rem;
  margin-bottom: -1px;
`;

const editablePlaceholderStyles = css`
  font-size: 1.25rem;
  margin-bottom: -1px;
`;

const headerContainerStyles = css`
  /* This causes the header dropdown to align with the header in editableModel, so there's no
   * janky-ness when it swaps over */
  margin-top: 5px;
`;
